import * as React from "react";
import formatDate from "dateformat";
import { useHighPopulationCovidCases } from "../../../../../hooks/static-queries/use-high-population-covid-cases";
import ContentCard from "../../../../ui-library/content-card/content-card";
import CardContent from "../../../../ui-library/content-card/card-content";
import CardHeading from "../../../../ui-library/content-card/card-heading";
import Sorter from "../../../../ui-library/sorter/sorter";
import { EventCategory } from "../../../../../utils/analytics";
import { useWorldCovidCases } from "../../../../../hooks/static-queries/use-world-covid-cases";
import SourceLink from "../../../../ui-library/source-link/source-link";
import { COVID_SOURCES } from "../../../../../constants/sources";
import CountrySituationTableHeader from "./country-situation-table-header";
import CountrySituationTableRow, {
  CountryData,
} from "./country-situation-table-row";
import styles from "./country-situation-table.styles";

type SortBy =
  | "A – Z"
  | "Total cases"
  | "Total deaths"
  | "Avg. new daily"
  | "Population size";

const sorters: SortBy[] = [
  "A – Z",
  "Total cases",
  "Total deaths",
  "Avg. new daily",
  "Population size",
];

const CountrySituationTable: React.FC = () => {
  const [activeSorter, setActiveSorter] = React.useState<SortBy>(sorters[0]);

  const worldData = useWorldCovidCases() as CountryData | undefined;

  const countriesData =
    (useHighPopulationCovidCases()?.filter(
      (c) =>
        c &&
        c.latest &&
        c.data &&
        c.latest.totalCases &&
        c.latest.totalCasesPerMillion &&
        (c.latest.totalCases > 150 || c.latest.totalCasesPerMillion > 30)
    ) as CountryData[] | undefined) || [];

  let sortedCountries: CountryData[] = [];

  if (activeSorter === "A – Z") {
    sortedCountries = countriesData.sort((a, b) => {
      if (b.displayName < a.displayName) {
        return 1;
      }
      if (a.displayName < b.displayName) {
        return -1;
      }
      return 0;
    });
  }

  if (activeSorter === "Population size") {
    sortedCountries = countriesData.sort(
      (a, b) => b.population! - a.population!
    );
  }

  if (activeSorter === "Avg. new daily") {
    sortedCountries = countriesData.sort(
      (a, b) => b.latest.averageNewCases - a.latest.averageNewCases
    );
  }

  if (activeSorter === "Total cases") {
    sortedCountries = countriesData.sort(
      (a, b) => (b.latest.totalCases || 0) - (a.latest.totalCases || 0)
    );
  }

  if (activeSorter === "Total deaths") {
    sortedCountries = countriesData.sort(
      (a, b) => (b.latest.totalDeaths || 0) - (a.latest.totalDeaths || 0)
    );
  }

  const lastDay =
    countriesData && countriesData[0] && countriesData[0].latest.date;

  if (!lastDay) {
    return null;
  }

  const headingDetail = `Last updated ${formatDate(lastDay, "dd mmm yyyy")}`;

  return (
    <ContentCard>
      <CardContent>
        <CardHeading headingDetail={headingDetail}>
          COVID-19 situation snapshots
        </CardHeading>
        <div css={styles.sectionHeading}>
          {sortedCountries.length} countries
          <Sorter
            items={sorters}
            value={activeSorter}
            onSelect={(value) => setActiveSorter(value as SortBy)}
            eventCategory={EventCategory.CountryControlCompareTable}
          />
        </div>

        <div css={styles.tableContainer}>
          <div css={styles.table}>
            <CountrySituationTableHeader />

            {worldData && (
              <CountrySituationTableRow
                key={worldData.displayName}
                countryData={worldData}
              />
            )}

            {sortedCountries.map((countryData) => (
              <CountrySituationTableRow
                key={countryData.displayName}
                countryData={countryData}
              />
            ))}

            <div css={styles.footer} />
          </div>
        </div>

        <SourceLink
          sources={[COVID_SOURCES.OWID]}
          isDataOnly={true}
          eventCategory={EventCategory.CountryPage}
        />
      </CardContent>
    </ContentCard>
  );
};

export default CountrySituationTable;
